import { Engine } from 'excalibur';
import { gameState } from './gameState';

// Simple DOM-based UI replacing the in-canvas UpgradePanel & Speed control
// Creates its own container appended to <body> so index.html doesn't need markup changes

import { recenterTower } from './tower';

export function initDomUi(engine: Engine) {
  // Create container
  const container = document.createElement('div');
  container.id = 'ui-container';
  container.innerHTML = `
    <div class="ui-panel">
      <div class="ui-row">HP: <span id="hp-value">0/0</span>
        <div class="bar hp"><div class="bar-fill hp" id="hp-bar"></div></div>
      </div>
      <div class="ui-row">MP: <span id="mp-value">0/0</span>
        <div class="bar mp"><div class="bar-fill mp" id="mp-bar"></div></div>
      </div>
      <div class="ui-row">Rage: <span id="rage-seconds">0.0s</span>
        <div class="bar rage"><div class="bar-fill rage" id="rage-bar"></div></div>
      </div>
      <div class="ui-row">Gold: <span id="gold-value">0</span></div>
      <div class="ui-row">Wave: <span id="wave-value">0</span> <span id="wave-status" style="opacity:0.8;"></span></div>
      <div id="upgrades-container" class="upgrades-grid">
        <div class="ui-row upgrade" id="row-damage"></div>
        <div class="ui-row upgrade" id="row-crit-chance"></div>
        <div class="ui-row upgrade" id="row-crit-mult"></div>
        <div class="ui-row upgrade" id="row-split-chance"></div>
        <div class="ui-row upgrade" id="row-split-dim"></div>
        <div class="ui-row upgrade" id="row-split-count"></div>
        <div class="ui-row upgrade" id="row-bounce-chance"></div>
        <div class="ui-row upgrade" id="row-bounce-dim"></div>
        <div class="ui-row upgrade" id="row-bounce-targets"></div>
        <div class="ui-row upgrade" id="row-attack-speed"></div>
        <div class="ui-row upgrade" id="row-range"></div>
        <div class="ui-row upgrade" id="row-rage-chance"></div>
        <div class="ui-row upgrade" id="row-rage-duration"></div>
      </div>
      <div class="ui-row speed" style="margin-top: 8px; margin-bottom: 8px">
        <label for="speed-range">Speed: <span id="speed-value">1.00x</span></label>
        <input id="speed-range" type="range" min="1" max="5" step="0.1" value="1">
      </div>
      <div class="ui-row">
        <button id="reset-btn" class="ui-btn danger">Reset Progress</button>
      </div>
    </div>
  `;
  document.body.appendChild(container);
  // Recenter tower now that the UI panel is present and has a measured width
  try { recenterTower(engine); } catch {}

  // Build table-like upgrade rows
  type RowParts = { update: () => void };
  const makeUpgradeRow = (
    id: string,
    name: string,
    getCurrent: () => string,
    getDelta: () => string,
    getCost: () => number,
    onBuy: () => void
  ): RowParts => {
    const row = container.querySelector(`#${id}`) as HTMLDivElement;
    row.classList.add('upgrade');

    const nameSpan = document.createElement('span');
    nameSpan.className = 'col-name';
    nameSpan.textContent = name;

    const currentSpan = document.createElement('span');
    currentSpan.className = 'col-current';

    const deltaSpan = document.createElement('span');
    deltaSpan.className = 'col-delta';

    const btn = document.createElement('button');
    btn.className = 'ui-btn price-btn';
    btn.addEventListener('click', () => onBuy());

    row.appendChild(nameSpan);
    row.appendChild(currentSpan);
    row.appendChild(deltaSpan);
    row.appendChild(btn);

    return {
      update: () => {
        currentSpan.textContent = getCurrent();
        deltaSpan.textContent = getDelta();
        const c = getCost();
        btn.textContent = `${c}g`;
        const affordable = gameState.canAfford(c);
        btn.disabled = !affordable;
        btn.classList.toggle('affordable', affordable);
      }
    };
  };

  // Use persisted costs from game state to prevent reload exploits
  const costs = gameState.costs;

  const updaters: Array<() => void> = [];

  updaters.push(makeUpgradeRow(
    'row-damage',
    'Damage',
    () => `${gameState.upgrades.damage}`,
    () => `+1`,
    () => costs.damage,
    () => {
      const cost = costs.damage;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.damage += 1;
        costs.damage = Math.floor(cost * 1.25 + 5);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-crit-chance',
    'Crit chance',
    () => `${Math.round(gameState.upgrades.critChance*100)}%`,
    () => `+2%`,
    () => costs.critChance,
    () => {
      const cost = costs.critChance;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.critChance = Math.min(1, gameState.upgrades.critChance + 0.02);
        costs.critChance = Math.floor(cost * 1.35 + 10);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-crit-mult',
    'Crit mult',
    () => `x${gameState.upgrades.critMultiplier.toFixed(2)}`,
    () => `+0.25`,
    () => costs.critMult,
    () => {
      const cost = costs.critMult;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.critMultiplier += 0.25;
        costs.critMult = Math.floor(cost * 1.35 + 10);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-split-chance',
    'Split chance',
    () => `${Math.round(gameState.upgrades.splitChance*100)}%`,
    () => `+5%`,
    () => costs.splitChance,
    () => {
      const cost = costs.splitChance;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.splitChance = Math.min(1, gameState.upgrades.splitChance + 0.05);
        costs.splitChance = Math.floor(cost * 1.35 + 20);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-split-dim',
    'Split damage',
    () => `x${(gameState.upgrades.splitDiminishing).toFixed(2)}`,
    () => `+0.05`,
    () => costs.splitDim,
    () => {
      const cost = costs.splitDim;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.splitDiminishing = gameState.upgrades.splitDiminishing + 0.05;
        costs.splitDim = Math.floor(cost * 1.25 + 10);
        gameState.saveToStorage();
      }
    }
  ).update);

  // New: Split count starting at 3, +1 per purchase
  updaters.push(makeUpgradeRow(
    'row-split-count',
    'Split count',
    () => `${gameState.upgrades.splitCount}`,
    () => `+1`,
    () => costs.splitCount,
    () => {
      const cost = costs.splitCount;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.splitCount += 1;
        costs.splitCount = Math.floor(cost * 1.3 + 15);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-bounce-chance',
    'Bounce chance',
    () => `${Math.round(gameState.upgrades.bounceChance*100)}%`,
    () => `+5%`,
    () => costs.bounceChance,
    () => {
      const cost = costs.bounceChance;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.bounceChance = Math.min(1, gameState.upgrades.bounceChance + 0.05);
        costs.bounceChance = Math.floor(cost * 1.35 + 20);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-bounce-dim',
    'Bounce damage',
    () => `x${(gameState.upgrades.bounceDiminishing).toFixed(2)}`,
    () => `+0.05`,
    () => costs.bounceDim,
    () => {
      const cost = costs.bounceDim;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.bounceDiminishing = gameState.upgrades.bounceDiminishing + 0.05;
        costs.bounceDim = Math.floor(cost * 1.3 + 15);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-bounce-targets',
    'Bounce targets',
    () => `${gameState.upgrades.bounceMaxTargets}`,
    () => `+1`,
    () => costs.bounceTargets,
    () => {
      const cost = costs.bounceTargets;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.bounceMaxTargets += 1;
        costs.bounceTargets = Math.floor(cost * 1.5 + 25);
        gameState.saveToStorage();
      }
    }
  ).update);

  // New: Attack speed and Range upgrades
  updaters.push(makeUpgradeRow(
    'row-attack-speed',
    'Attack speed',
    () => `${Math.round(gameState.upgrades.attackSpeedMult*100)}%`,
    () => `+10%`,
    () => costs.attackSpeed,
    () => {
      const cost = costs.attackSpeed;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.attackSpeedMult += 0.10; // additive multiplier
        costs.attackSpeed = Math.floor(cost * 1.4 + 30);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-range',
    'Range',
    () => `${gameState.upgrades.rangeBonus}`,
    () => `+10`,
    () => costs.range,
    () => {
      const cost = costs.range;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.rangeBonus += 10;
        costs.range = Math.floor(cost * 1.35 + 20);
        gameState.saveToStorage();
      }
    }
  ).update);

  // Rage upgrades
  updaters.push(makeUpgradeRow(
    'row-rage-chance',
    'Rage chance',
    () => `${Math.round(gameState.upgrades.rageChance*100)}%`,
    () => `+5%`,
    () => costs.rageChance,
    () => {
      const cost = costs.rageChance;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.rageChance = Math.min(1, gameState.upgrades.rageChance + 0.05);
        costs.rageChance = Math.floor(cost * 1.35 + 25);
        gameState.saveToStorage();
      }
    }
  ).update);

  updaters.push(makeUpgradeRow(
    'row-rage-duration',
    'Rage duration',
    () => `${gameState.upgrades.rageDuration.toFixed(1)}s`,
    () => `+0.1s`,
    () => costs.rageDuration,
    () => {
      const cost = costs.rageDuration;
      if (gameState.trySpend(cost)) {
        gameState.upgrades.rageDuration = Math.min(30, gameState.upgrades.rageDuration + 0.1);
        costs.rageDuration = Math.floor(cost * 1.3 + 20);
        gameState.saveToStorage();
      }
    }
  ).update);

  // Speed slider
  const speedRange = container.querySelector('#speed-range') as HTMLInputElement;
  const speedValue = container.querySelector('#speed-value') as HTMLSpanElement;
  const hpSpan = container.querySelector('#hp-value') as HTMLSpanElement;
  const mpSpan = container.querySelector('#mp-value') as HTMLSpanElement;
  const hpBar = container.querySelector('#hp-bar') as HTMLDivElement;
  const mpBar = container.querySelector('#mp-bar') as HTMLDivElement;
  const rageBar = container.querySelector('#rage-bar') as HTMLDivElement;
  const rageSecSpan = container.querySelector('#rage-seconds') as HTMLSpanElement;
  const goldSpan = container.querySelector('#gold-value') as HTMLSpanElement;
  const waveSpan = container.querySelector('#wave-value') as HTMLSpanElement;
  const waveStatusSpan = container.querySelector('#wave-status') as HTMLSpanElement;
  const resetBtn = container.querySelector('#reset-btn') as HTMLButtonElement;
  resetBtn.addEventListener('click', () => {
    if (confirm('Reset all saved progress?')) {
      gameState.clearStorage();
      location.reload();
    }
  });
  const syncSpeed = () => {
    const s = Math.max(1, Math.min(5, engine.timescale));
    speedRange.value = String(s);
    speedValue.textContent = `${s.toFixed(2)}x`;
  };
  speedRange.addEventListener('input', () => {
    const v = parseFloat(speedRange.value);
    const s = Math.max(1, Math.min(5, Math.round(v*10)/10));
    engine.timescale = s;
    gameState.timescale = s;
    try { gameState.saveToStorage(); } catch {}
    syncSpeed();
  });

  // Update loop for buttons, HP/MP, and speed label
  const update = () => {
    // Buttons
    for (const u of updaters) u();

    // HP/MP from gameState (tower vitals are synced into gameState by Tower)
    const hp = Math.max(0, Math.ceil(gameState.hp));
    const maxHp = Math.max(1, Math.ceil(gameState.maxHp));
    const mp = Math.max(0, Math.floor(gameState.mana));
    const maxMp = Math.max(1, Math.floor(gameState.maxMana));
    hpSpan.textContent = `${hp}/${maxHp}`;
    mpSpan.textContent = `${mp}/${maxMp}`;
    // Bars
    const hpPct = Math.max(0, Math.min(1, maxHp > 0 ? hp / maxHp : 0));
    const mpPct = Math.max(0, Math.min(1, maxMp > 0 ? mp / maxMp : 0));
    if (hpBar) hpBar.style.width = `${(hpPct * 100).toFixed(1)}%`;
    if (mpBar) mpBar.style.width = `${(mpPct * 100).toFixed(1)}%`;

    // Gold & Wave (moved from canvas HUD)
    goldSpan.textContent = String(gameState.gold);
    waveSpan.textContent = `${gameState.wave}`;
    const ws = gameState.waveStatus?.trim();
    waveStatusSpan.textContent = ws ? `(${ws})` : '';

    // Rage bar fill: orange progress based on remaining rage time
    let ragePct = 0;
    let rageSecs = 0;
    if (gameState.rageActive && gameState.rageMsTotal > 0) {
      ragePct = Math.max(0, Math.min(1, gameState.rageMsLeft / gameState.rageMsTotal));
      rageSecs = Math.max(0, gameState.rageMsLeft / 1000);
    } else {
      rageSecs = 0;
    }
    if (rageBar) rageBar.style.width = `${(ragePct * 100).toFixed(1)}%`;
    if (rageSecSpan) rageSecSpan.textContent = `${rageSecs.toFixed(1)}s`;

    // Speed
    syncSpeed();
    requestAnimationFrame(update);
  };
  update();
}
